/**
 * ScamBlocker AI — Self-Servis Kurumsal Kayıt Sayfası (/register)
 * Admin token gerekmeden yeni org oluşturur; starter plan, anında JWT.
 */

import { Building2, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { registerApi } from '../api/authApi';
import styles from './Register.module.css';

export function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const PLAN_OPTIONS = [
    { value: 'starter', label: t('register.planStarter') },
    { value: 'professional', label: t('register.planProfessional') },
    { value: 'enterprise', label: t('register.planEnterprise') },
  ];

  // Form alanları
  const [orgName, setOrgName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [planCode, setPlanCode] = useState('starter');
  const [seats, setSeats] = useState(5);
  const [showPwd, setShowPwd] = useState(false);

  // UI durumu
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [apiKey, setApiKey] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await registerApi({
        org_name: orgName.trim(),
        contact_email: contactEmail.trim(),
        owner_name: ownerName.trim(),
        owner_email: ownerEmail.trim(),
        owner_password: ownerPassword,
        plan_code: planCode,
        seats,
      });
      setApiKey(data.api_key ?? '');
      setSuccess(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('register.errorGeneric');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.successBox}>
          <CheckCircle2 size={40} className={styles.successIcon} />
          <h2 className={styles.successTitle}>Hesabınız Oluşturuldu!</h2>
          <p className={styles.successSub}>
            Kurumunuz başarıyla kaydedildi. Aşağıdaki API anahtarını güvenli bir yere kaydedin — bir
            daha gösterilmeyecektir.
          </p>

          {apiKey && (
            <div className={styles.apiKeyBox}>
              <p className={styles.apiKeyLabel}>{t('register.apiKeyLabel')}</p>
              <code className={styles.apiKeyValue}>{apiKey}</code>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={() => navigator.clipboard.writeText(apiKey)}
              >
                {t('register.copy')}
              </button>
            </div>
          )}

          <button type="button" className={styles.submitBtn} onClick={() => navigate('/login')}>
            Panele Giriş Yap
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Building2 size={32} className={styles.icon} />
        <h2 className={styles.title}>Kurumsal Kayıt</h2>
        <p className={styles.subtitle}>ScamBlocker AI B2B platformuna ücretsiz başlayın</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        {/* ── Kurum bilgileri ── */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>{t('register.orgInfoLegend')}</legend>

          <div className={styles.field}>
            <label htmlFor="orgName" className={styles.label}>
              {t('register.orgNameLabel')} <span className={styles.required}>*</span>
            </label>
            <input
              id="orgName"
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Şirket A.Ş."
              required
              className={styles.input}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="contactEmail" className={styles.label}>
              {t('register.contactEmailLabel')} <span className={styles.required}>*</span>
            </label>
            <input
              id="contactEmail"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="iletisim@sirket.com"
              required
              autoComplete="email"
              className={styles.input}
            />
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="planCode" className={styles.label}>
                Plan
              </label>
              <select
                id="planCode"
                value={planCode}
                onChange={(e) => setPlanCode(e.target.value)}
                className={styles.input}
              >
                {PLAN_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.fieldSmall}>
              <label htmlFor="seats" className={styles.label}>
                Kullanıcı Sayısı
              </label>
              <input
                id="seats"
                type="number"
                min={1}
                max={500}
                value={seats}
                onChange={(e) => setSeats(Number(e.target.value))}
                className={styles.input}
              />
            </div>
          </div>
        </fieldset>

        {/* ── Yönetici hesabı ── */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>{t('register.ownerAccountLegend')}</legend>

          <div className={styles.field}>
            <label htmlFor="ownerName" className={styles.label}>
              {t('register.ownerNameLabel')} <span className={styles.required}>*</span>
            </label>
            <input
              id="ownerName"
              type="text"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="Ahmet Yılmaz"
              required
              autoComplete="name"
              className={styles.input}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="ownerEmail" className={styles.label}>
              {t('register.ownerEmailLabel')} <span className={styles.required}>*</span>
            </label>
            <input
              id="ownerEmail"
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="yonetici@sirket.com"
              required
              autoComplete="email"
              className={styles.input}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="ownerPassword" className={styles.label}>
              {t('register.passwordLabel')} <span className={styles.required}>*</span>
            </label>
            <div className={styles.passwordWrap}>
              <input
                id="ownerPassword"
                type={showPwd ? 'text' : 'password'}
                value={ownerPassword}
                onChange={(e) => setOwnerPassword(e.target.value)}
                placeholder="En az 8 karakter"
                required
                minLength={8}
                autoComplete="new-password"
                className={styles.input}
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </fieldset>

        <p className={styles.legal}>
          {t('register.legalPrefix')}{' '}
          <a href="/kvkk" className={styles.legalLink} target="_blank" rel="noreferrer">
            {t('register.legalKvkk')}
          </a>{' '}
          {t('register.legalAnd')}{' '}
          <a href="/terms" className={styles.legalLink} target="_blank" rel="noreferrer">
            {t('register.legalTerms')}
          </a>
          {t('register.legalSuffix')}
        </p>

        <button type="submit" className={styles.submitBtn} disabled={loading}>
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {t('register.creatingAccount')}
            </>
          ) : (
            t('register.submit')
          )}
        </button>
      </form>

      <p className={styles.footer}>
        {t('register.alreadyHaveAccount')}{' '}
        <a href="/login" className={styles.link}>
          {t('register.login')}
        </a>
      </p>
    </div>
  );
}
